import { Link } from "@tanstack/react-router";
import { resolveImage } from "@/lib/image-map";
import type { Product, Creator } from "@/lib/queries";

interface Props {
  products: Product[];
  creator?: Creator;
}

export function PhoneMockup({ products, creator }: Props) {
  const placeholders: Product[] = Array.from({ length: 4 }).map((_, i) => ({
    id: `placeholder-${i}`,
    title: ["Cashmere crew", "Linen tote", "Silk slip dress", "Ceramic carafe"][i],
    brand: ["Atelier", "Maison", "Studio", "Heritage"][i],
    price: [180, 95, 240, 65][i],
    image_key: "",
    network: "levanta",
    in_stock: true,
  } as unknown as Product));
  const source = products.length ? products : placeholders;
  const feed = [...source, ...source];

  return (
    <div className="relative w-full h-full flex items-center justify-center">
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-[48%] h-[55%] w-[55%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accent/20 blur-[80px]"
      />

      <div
        className="relative"
        style={{ width: "min(78%, 280px)", aspectRatio: "433 / 882" }}
      >
        <svg
          aria-label="iPhone 15 Pro"
          fill="none"
          viewBox="0 0 433 882"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute inset-0 h-full w-full pointer-events-none"
        >
          <path className="fill-ink" d="M2 73C2 32.6832 34.6832 0 75 0H357C397.317 0 430 32.6832 430 73V809C430 849.317 397.317 882 357 882H75C34.6832 882 2 849.317 2 809V73Z" />
          <path className="fill-ink" d="M0 171C0 170.448 0.447715 170 1 170H3V204H1C0.447715 204 0 203.552 0 203V171Z" />
          <path className="fill-ink" d="M1 234C1 233.448 1.44772 233 2 233H3.5V300H2C1.44772 300 1 299.552 1 299V234Z" />
          <path className="fill-ink" d="M1 319C1 318.448 1.44772 318 2 318H3.5V385H2C1.44772 385 1 384.552 1 384V319Z" />
          <path className="fill-ink" d="M430 279H432C432.552 279 433 279.448 433 280V384C433 384.552 432.552 385 432 385H430V279Z" />
          <path className="fill-ink" d="M6 74C6 35.3401 37.3401 4 76 4H356C394.66 4 426 35.3401 426 74V808C426 846.66 394.66 878 356 878H76C37.3401 878 6 846.66 6 808V74Z" />
          <path className="fill-clay" d="M174 5H258V5.5C258 6.60457 257.105 7.5 256 7.5H176C174.895 7.5 174 6.60457 174 5.5V5Z" opacity="0.5" />
          <path className="fill-ink stroke-ink stroke-[0.5]" d="M21.25 75C21.25 44.2101 46.2101 19.25 77 19.25H355C385.79 19.25 410.75 44.2101 410.75 75V807C410.75 837.79 385.79 862.75 355 862.75H77C46.2101 862.75 21.25 837.79 21.25 807V75Z" />
          <path className="fill-background/10" d="M154 48.5C154 38.2827 162.283 30 172.5 30H259.5C269.717 30 278 38.2827 278 48.5C278 58.7173 269.717 67 259.5 67H172.5C162.283 67 154 58.7173 154 48.5Z" />
          <path className="fill-background/20" d="M249 48.5C249 42.701 253.701 38 259.5 38C265.299 38 270 42.701 270 48.5C270 54.299 265.299 59 259.5 59C253.701 59 249 54.299 249 48.5Z" />
          <path className="fill-clay" d="M254 48.5C254 45.4624 256.462 43 259.5 43C262.538 43 265 45.4624 265 48.5C265 51.5376 262.538 54 259.5 54C256.462 54 254 51.5376 254 48.5Z" />
        </svg>

        {/* HTML screen overlay — positioned over the SVG screen rectangle.
            Avoids Safari/iOS bugs with HTML inside SVG <foreignObject>. */}
        <div
          className="absolute overflow-hidden bg-background"
          style={{
            left: "4.908%",
            top: "2.183%",
            right: "5.139%",
            bottom: "2.183%",
            borderRadius: "14.3% / 6.6%",
          }}
        >
          <div className="absolute left-1/2 top-2 z-30 h-4 w-20 -translate-x-1/2 rounded-full bg-black" aria-hidden />
          <div className="absolute inset-x-0 top-9 z-20 border-b border-border/40 bg-background/80 px-4 py-2.5 backdrop-blur-md">
            <div className="flex items-center justify-between">
              <p className="font-serif text-lg tracking-tight">Pluggz.</p>
              <div className="flex items-center gap-1.5 rounded-full bg-destructive/10 px-2 py-0.5">
                <span className="h-1.5 w-1.5 rounded-full bg-destructive animate-pulse" />
                <span className="text-[8px] uppercase tracking-widest text-destructive">Live</span>
              </div>
            </div>
          </div>
          <div className="absolute inset-x-0 top-[68px] bottom-0 overflow-hidden">
            <div className="pointer-events-none absolute inset-x-0 top-0 z-20 h-6 bg-gradient-to-b from-background to-transparent" aria-hidden />
            <div className="phone-scroll flex flex-col gap-3 px-3 pt-3">
              {feed.map((p, i) => (
                <Link
                  key={`${p.id}-${i}`}
                  to="/product/$id"
                  params={{ id: p.id }}
                  aria-label={`${p.title} by ${p.brand}, £${p.price}`}
                  tabIndex={i < products.length ? 0 : -1}
                  aria-hidden={i >= products.length ? true : undefined}
                  className="block overflow-hidden rounded-xl border border-border/40 bg-card shadow-sm"
                >
                  <div className="relative aspect-[5/4] overflow-hidden bg-muted">
                    <img
                      src={resolveImage(p.image_key)}
                      alt={p.title}
                      width={280}
                      height={350}
                      decoding="async"
                      loading={i < 3 ? "eager" : "lazy"}
                      fetchPriority={i === 0 ? "high" : undefined}
                      className="h-full w-full object-cover"
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
                    />
                    {creator && i % 3 === 0 && (
                      <div className="absolute left-2 top-2 flex items-center gap-1.5 rounded-full bg-background/90 px-2 py-0.5 backdrop-blur">
                        <img src={resolveImage(creator.avatar_url)} alt={creator.name} loading="lazy" decoding="async" className="h-3.5 w-3.5 rounded-full object-cover" />
                        <span className="text-[8px] font-medium">@{creator.handle}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between px-2.5 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-[9px] uppercase tracking-wider text-muted-foreground">{p.brand}</p>
                      <p className="truncate font-serif text-[11px] leading-tight">{p.title}</p>
                    </div>
                    <p className="shrink-0 font-serif text-[11px]">{p.price ? `£${p.price}` : ""}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-16 bg-gradient-to-t from-background via-background/80 to-transparent" aria-hidden />
          <div className="absolute bottom-1.5 left-1/2 z-30 h-1 w-24 -translate-x-1/2 rounded-full bg-foreground/80" aria-hidden />
        </div>
      </div>
    </div>
  );
}
